import { isProductInStock } from 'components/ProductDetails';
import { notifyError } from 'config/setupBugsnag';
import getConfig from 'next/config';

import {
  CategoryFilter,
  ITaxon,
  JsonApiDocument,
  JsonApiListResponse,
  Menus,
  MenuStructure,
  Product,
  PromotedTaxon,
  Taxon,
  TaxonPageAttributes,
  TBreadCrumb,
} from 'types';
import RestUtility from '../../restUtility';
import { menuEndPoint, taxonEndPoint } from '../../restUtility/restEndpoints';

export class TaxonService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async getTaxonDetails(taxonSlug: string) {
    try {
      const includedFields = 'children';
      const { publicRuntimeConfig } = getConfig();

      const taxonFields = 'name,permalink,breadcrumbs,is_child';
      let url = `${taxonEndPoint}/${taxonSlug}?fields[taxon]=${taxonFields}`;
      if (publicRuntimeConfig.STORE_REGION !== 'NA') {
        url += `&include=${includedFields}`;
      }
      const taxonResponse = await this.restUtility.get<ITaxon>(url);

      return {
        taxon: this.buildTaxonProps(taxonResponse.data),
        taxonFilters: {
          categories: this.buildCategoryFilters(taxonResponse),
        },
      };
    } catch (error) {
      notifyError('ApiError:: getTaxonDetails', error);
      throw error;
    }
  }

  async getProductsByTaxonSlug(taxonSlug: string) {
    try {
      const includedFields =
        'children,children.taxons,children.products,children.products.images,products,products.images';
      const productFields =
        'sku,slug,display_price,name,price,images,default_variant,in_stock,backorderable,is_having_bulk_discount';
      const imageFields = 'original_url';
      const taxonFields =
        'name,permalink,breadcrumbs,user_elligible_product_ids';
      const taxonResponse = await this.restUtility.get<ITaxon>(
        `${taxonEndPoint}/${taxonSlug}?include=${includedFields}&fields[product]=${productFields}&fields[image]=${imageFields}&fields[taxon]=${taxonFields}`
      );

      return {
        taxon: this.buildTaxonProps(taxonResponse.data),
        products: this.buildProducts(taxonResponse),
        filters: {
          categories: this.buildCategoryFilters(taxonResponse),
        },
      };
    } catch (error) {
      notifyError('ApiError:: getProductsByTaxonSlug', error);
      throw error;
    }
  }

  buildCategoryFilters(response: ITaxon) {
    const categoryFilters: CategoryFilter[] = [];

    if (response.included && response.included?.length > 0) {
      const taxons = response.included.filter(item => item.type === 'taxon');

      taxons.forEach(taxon => {
        const taxonProductIds =
          taxon.attributes?.user_elligible_product_ids?.map((id: number) =>
            id.toString()
          );
        categoryFilters.push({
          categoryId: taxon.id.toString(),
          categoryName: taxon.attributes.name,
          categorySlug: taxon.attributes.permalink,
          categoryBreadcrumbs: this.buildProductBreadcrumb(
            taxon.attributes.breadcrumbs
          ),
          productIds: taxonProductIds,
        });
      });
    }
    return categoryFilters;
  }

  buildTaxonProps(response: JsonApiDocument) {
    const taxonDetails: Taxon = {
      title: '',
      permalink: '',
      breadCrumb: [],
    };

    taxonDetails.title = response.attributes.name;
    taxonDetails.permalink = response.attributes.permalink;
    taxonDetails.breadCrumb = this.buildProductBreadcrumb(
      response.attributes.breadcrumbs
    );
    return taxonDetails;
  }

  buildProductBreadcrumb(breadcrumbs: TBreadCrumb[]) {
    const breadcrumbItems = [...breadcrumbs].sort(
      (item1, item2) => item1.level - item2.level
    );

    const homepage: TBreadCrumb = {
      name: 'Store Home',
      permalink: '/',
      id: -1,
      level: -1,
    };
    breadcrumbItems.unshift(homepage);

    return breadcrumbItems;
  }

  async getPromotedTaxons() {
    try {
      const response = await this.restUtility.get<JsonApiListResponse>(
        `${taxonEndPoint}?filter[header_image_enable]=true&include=header_image`
      );

      return this.buildPromotedTaxons(response);
    } catch (error) {
      notifyError('ApiError:: getPromotedTaxons', error);
      throw error;
    }
  }

  buildPromotedTaxons(response: JsonApiListResponse) {
    const { publicRuntimeConfig } = getConfig();

    const taxons = [...response.data].sort((taxon1, taxon2) => {
      return taxon2.attributes.header_image_position >
        taxon1.attributes.header_image_position
        ? -1
        : 1;
    });

    if (taxons.length > 3) {
      taxons.pop();
    }

    const images = response.included;
    const promotedTaxon: PromotedTaxon[] = [];

    taxons.forEach(taxon => {
      const image = images?.filter(img => {
        return (
          img.attributes.viewable_id.toString() === taxon.id.toString() &&
          img.type === 'taxon_header_image'
        );
      })[0];

      if (image) {
        promotedTaxon.push({
          h1Text: taxon.attributes.h1_text,
          h1TextPosition: taxon.attributes.h1_text_label_position,
          h3Text: taxon.attributes.h3_text,
          h3TextPosition: taxon.attributes.h3_text_label_position,
          permalink: taxon.attributes.permalink,
          imageUrl: `${publicRuntimeConfig.IMAGE_HOST}${image.attributes.normal_image}`,
        });
      }
    });
    return promotedTaxon;
  }

  async getMenu() {
    try {
      const response = await this.restUtility.get<Menus>(
        `${menuEndPoint}?include=menu_items,menu_items.icon`
      );
      return this.buildMenuStructureForClient(response);
    } catch (error) {
      notifyError('ApiError:: getMenu', error);
      throw error;
    }
  }

  buildMenuStructureForClient(response: Menus) {
    const menuStructure: MenuStructure = {
      id: response.data[0].id,
      name: response.data[0].attributes.name,
      link: '',
      isLeaf: false,
      children: [],
    };

    const level1Items = response.included
      .filter(
        menuItem =>
          menuItem.attributes.depth === 1 &&
          menuItem.attributes.link?.length > 0
      )
      .sort((item1, item2) => item1.attributes.lft - item2.attributes.lft);

    const level2Items = response.included.filter(
      menuItem =>
        menuItem.attributes.depth === 2 && menuItem.attributes.link?.length > 0
    );

    level1Items.forEach(menuItem => {
      const l1Item: MenuStructure = {
        id: menuItem.id,
        name: menuItem.attributes.name,
        link: this.buildTaxonPageLink(menuItem.attributes.link),
        isLeaf: menuItem.attributes.is_leaf,
        children: [],
      };

      const children = menuItem.relationships.children.data.map(
        item => item.id
      );

      children.forEach(menuItemId => {
        const childMenuItem = level2Items.find(
          menuItem => menuItem.id === menuItemId
        );
        if (childMenuItem) {
          l1Item.children.push({
            id: childMenuItem.id,
            name: childMenuItem.attributes.name,
            link: this.buildTaxonPageLink(childMenuItem.attributes.link),
            isLeaf: childMenuItem.attributes.is_leaf,
            children: [],
          });
        }
      });
      menuStructure.children.push(l1Item);
    });

    return menuStructure;
  }

  buildProducts(response: ITaxon) {
    const transformedPrdouctsResponse: Product[] = [];
    const { publicRuntimeConfig } = getConfig();

    if (response.included && response.included?.length > 0) {
      const products = response.included.filter(
        item => item.type === 'product'
      );

      const images = response.included.filter(item => item.type === 'image');

      products.forEach(product => {
        const productImageIds =
          product.relationships.images.data.map(
            (image: { id: number; type: 'image' }) => image.id
          ) ?? [];

        const productImage = images.filter(image =>
          productImageIds.includes(image.id)
        )[0];

        const thumbnail = productImage
          ? `${publicRuntimeConfig.IMAGE_HOST}${productImage.attributes.original_url}`
          : '/images/noimage.jpeg';
        const original = productImage
          ? `${publicRuntimeConfig.IMAGE_HOST}${productImage.attributes.original_url}`
          : '/images/noimage.jpeg';

        transformedPrdouctsResponse.push({
          id: product.id,
          AvailableOn: product.attributes.available_on,
          quantityLimit: product.attributes.quantity_limit,
          variantId: product.relationships.default_variant.data.id,
          slug: product.attributes.slug,
          name: product.attributes.name,
          price: product.attributes.price,
          displayPrice: product.attributes.display_price,
          description: product.attributes.description,
          sku: product.attributes.sku,

          isHavingBulkDiscount: product.attributes.is_having_bulk_discount,

          bulkDiscount: {
            title: '',
            description: '',
          },
          inStock: isProductInStock(
            product.attributes.backorderable,
            product.attributes.in_stock
          ),
          breadcrumb: [],
          images: [
            {
              id: productImage?.id.toString() ?? '',
              thumbnail: thumbnail,
              original: original,
            },
          ],
          languageBasedVariants: [],
          assets: [],
          materialType: product.attributes.material_type,
          isCustomizable: product.attributes.is_customizable,
          discontinueOn: product.attributes.discontinue_on,
          dimensions: '',
        });
      });
    }
    const doctorEligbleProducts = this.getDoctorEligibleProducts(
      response,
      transformedPrdouctsResponse
    );
    return doctorEligbleProducts;
  }

  getDoctorEligibleProducts(
    response: ITaxon,
    transformedPrdouctsResponse: Product[]
  ) {
    let doctorEligibleProductIds: number[] =
      response.data.attributes.user_elligible_product_ids ?? [];

    if (response.included && response.included?.length > 0) {
      const taxons = response.included.filter(item => item.type === 'taxon');
      taxons.forEach(taxon => {
        doctorEligibleProductIds = [
          ...doctorEligibleProductIds,
          ...taxon.attributes.user_elligible_product_ids,
        ];
      });
    }

    return transformedPrdouctsResponse.filter(product =>
      doctorEligibleProductIds.includes(parseInt(product.id))
    );
  }

  buildTaxonPageLink(permalink: string) {
    return permalink ? permalink.replace('/t/', '/category/') : permalink;
  }
}
